import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Receipt, Check, X, Banknote, FileText } from "lucide-react";
import { Button } from "../../components/ui/button";
import { invoicesApi, formatApiError } from "../../lib/ledgerApi";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "sonner";

const STATUS_PILL = {
  issued: "border-status-info/40 bg-status-info/10 text-status-info",
  paid: "border-status-ok/40 bg-status-ok/10 text-status-ok",
  cancelled: "border-border bg-muted text-muted-foreground",
  overdue: "border-status-tampered/40 bg-status-tampered/10 text-status-tampered",
};

const fmt = (s) => {
  try {
    const n = Number(s);
    return Number.isFinite(n) ? n.toLocaleString(undefined, { maximumFractionDigits: 6 }) : s;
  } catch { return s; }
};

export default function InvoiceDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { business } = useAuth();
  const [inv, setInv] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      setInv(await invoicesApi.get(id));
    } catch (e) {
      toast.error("Load failed", { description: formatApiError(e.response?.data?.detail) });
    }
  };

  useEffect(() => {
    load();
  }, [id]); // eslint-disable-line

  if (!inv) return <div className="px-6 py-10 text-sm text-muted-foreground">Loading…</div>;

  const isPayer = inv.payer_business_id === business?.id;
  const isIssuer = inv.issuer_business_id === business?.id;
  const canApprove = isPayer && (inv.status === "issued" || inv.status === "overdue");
  const canCancel = isIssuer && (inv.status === "issued" || inv.status === "overdue");

  const onApprove = async () => {
    setBusy(true);
    try {
      const r = await invoicesApi.approve(id);
      toast.success("Approved & paid", {
        description: r.contract?.id ? `Contract ${r.contract.id.slice(0, 12)}…` : "",
      });
      await load();
    } catch (e) {
      toast.error("Approve failed", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setBusy(false);
    }
  };

  const onCancel = async () => {
    setBusy(true);
    try {
      await invoicesApi.cancel(id);
      toast.success("Cancelled");
      await load();
    } catch (e) {
      toast.error("Cancel failed", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-4xl px-6 md:px-10 py-10 md:py-14" data-testid="invoice-detail">
      <button onClick={() => nav("/app/invoices")} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-6">
        <ArrowLeft className="w-3 h-3" strokeWidth={1.5} /> Back to invoices
      </button>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-2">/ Invoice</div>
          <h1 className="text-2xl sm:text-3xl tracking-tight font-medium font-mono break-all" data-testid="invoice-id">{inv.id}</h1>
        </div>
        <span className={`px-3 py-1.5 text-sm border rounded-sm uppercase tracking-widest ${STATUS_PILL[inv.status]}`}>{inv.status}</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <div className="border border-border rounded-sm bg-card p-4">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Issuer</div>
          <div className="font-medium">{inv.issuer_legal_name}</div>
        </div>
        <div className="border border-border rounded-sm bg-card p-4">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Payer</div>
          <div className="font-medium">{inv.payer_legal_name}</div>
        </div>
        <div className="border border-border rounded-sm bg-card p-4">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Amount</div>
          <div className="font-mono text-xl">{fmt(inv.amount)} <span className="text-sm text-muted-foreground">{inv.currency}</span></div>
        </div>
      </div>

      {inv.memo && (
        <div className="border border-border rounded-sm bg-card p-4 mb-3">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Memo</div>
          <div className="text-sm">{inv.memo}</div>
        </div>
      )}
      {inv.due_date && (
        <div className="text-xs font-mono text-muted-foreground mb-6">due {new Date(inv.due_date).toLocaleString()}</div>
      )}
      {inv.contract_id && (
        <div className="border border-border rounded-sm bg-card p-4 mb-6">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Linked contract</div>
          <Link to={`/app/contracts/${inv.contract_id}`} className="text-sm font-mono hover:underline" data-testid="invoice-contract-link">
            <FileText className="inline w-3 h-3 mr-1" strokeWidth={1.5} />
            {inv.contract_id}
          </Link>
        </div>
      )}

      <div className="flex gap-2">
        {canApprove && (
          <Button onClick={onApprove} disabled={busy} data-testid="invoice-approve" className="rounded-sm">
            <Check className="w-3 h-3 mr-1" strokeWidth={1.5} /> Approve & pay
          </Button>
        )}
        {canCancel && (
          <Button variant="outline" onClick={onCancel} disabled={busy} data-testid="invoice-cancel" className="rounded-sm hover:border-status-tampered hover:text-status-tampered">
            <X className="w-3 h-3 mr-1" strokeWidth={1.5} /> Cancel invoice
          </Button>
        )}
      </div>
    </div>
  );
}
